import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useMemo } from "react";

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import { LineChart } from "../../Common/LineChart";
import { GraphPoint } from "../../types";
import { LineChartTooltipDisplay } from "./LineChartTooltip";

type ChartDataPoint = {
  x: string;
  y: number | null;
};

export type TradingByLineChartProps = {
  title: string;
  type?: "usd" | "lots" | "clients";
  data: GraphPoint[];
  unit?: string;
  classes?: string;
  format?: (value: number) => string;
  tooltipFormat?: (value: number) => string;
  startDate?: string;
  endDate?: string;
  onHover?: (pointer: { x: number; y: number }) => void;
};

export const TradingByLineChart = ({
  title,
  type = "usd",
  data,
  unit,
  classes,
  format,
  tooltipFormat,
  startDate,
  endDate,
  onHover,
}: TradingByLineChartProps) => {
  // Determine date grouping based on date range
  const dateGrouping = useMemo(
    () => determineDateGrouping(startDate, endDate),
    [startDate, endDate],
  );

  // Process chart data with proper date formatting
  const source = useMemo(() => {
    if (!data?.length) {
      return [];
    }

    // Format data points with consistent date format
    const formattedData = data.map((item) => ({
      x: formatToDayDate(item.date, dateGrouping, false, startDate, endDate),
      y: item.value,
      dayFormatted: formatToDayDate(
        item.date,
        dateGrouping,
        true,
        startDate,
        endDate,
      ),
    })) as ChartDataPoint[];

    // Track all dates for checking if start/end dates already exist
    const dates = formattedData.map((point) => dayjs(point.x));

    // Initialize chart data structure
    const chartData = [
      {
        // Nivo caches by ID, so we need to generate a random one each time
        // to ensure the chart updates when data changes
        // using performance.now() to get a more unique value in case of rapid re-renders and doesnt need complicated string manipulation
        id: performance.now().toString(),
        color: "rgba(107, 165, 252, 1)",
        data: [...formattedData],
        tickValues: [] as string[],
      },
    ];

    // Create labels that show user selection boundaries + data-smart intermediate labels
    const tickValues: string[] = [];

    // Always add the start date if specified
    if (startDate) {
      const formattedStartDate = formatToDayDate(startDate, dateGrouping);
      tickValues.push(formattedStartDate);
      const startDateObj = dayjs(formattedStartDate);

      // Add null data point at start to extend x-axis range
      if (!dates.some((d) => d.isSame(startDateObj, "day"))) {
        chartData[0].data.push({
          x: formattedStartDate,
          y: null,
        });
      }
    }

    // Add intermediate labels based on actual data distribution (data-smart approach)
    // Only add if they're not the same as start/end dates

    // Always add the first actual data point if it exists and is different from start
    if (formattedData.length > 0) {
      const firstDataLabel = formattedData[0].x;
      if (!tickValues.includes(firstDataLabel)) {
        tickValues.push(firstDataLabel);
      }
    }

    // Add intermediate labels based on actual data distribution
    if (formattedData.length > 2) {
      const dataLength = formattedData.length;

      // Calculate indices for intermediate points
      const quarterIndex = Math.floor(dataLength / 4);
      const midIndex = Math.floor(dataLength / 2);
      const threeQuarterIndex = Math.floor((dataLength * 3) / 4);

      // Add quarter point if it's not the same as other points
      if (quarterIndex > 0 && quarterIndex < dataLength - 1) {
        const quarterLabel = formattedData[quarterIndex].x;
        if (!tickValues.includes(quarterLabel)) {
          tickValues.push(quarterLabel);
        }
      }

      // Add middle point if it's not the same as other points
      if (midIndex > 0 && midIndex < dataLength - 1) {
        const midLabel = formattedData[midIndex].x;
        if (!tickValues.includes(midLabel)) {
          tickValues.push(midLabel);
        }
      }

      // Add 3/4 point if it's not the same as other points
      if (threeQuarterIndex > 0 && threeQuarterIndex < dataLength - 1) {
        const threeQuarterLabel = formattedData[threeQuarterIndex].x;
        if (!tickValues.includes(threeQuarterLabel)) {
          tickValues.push(threeQuarterLabel);
        }
      }
    }

    // Always add the last actual data point if it exists and is different from end
    if (formattedData.length > 1) {
      const lastDataLabel = formattedData[formattedData.length - 1].x;
      if (!tickValues.includes(lastDataLabel)) {
        tickValues.push(lastDataLabel);
      }
    }

    // Always add the end date if specified (and different from what we already have)
    if (endDate) {
      const formattedEndDate = formatToDayDate(endDate, dateGrouping);
      if (!tickValues.includes(formattedEndDate)) {
        tickValues.push(formattedEndDate);
      }
      const endDateObj = dayjs(formattedEndDate);

      // Add null data point at end to extend x-axis range
      if (!dates.some((d) => d.isSame(endDateObj, "day"))) {
        chartData[0].data.push({
          x: formattedEndDate,
          y: null,
        });
      }
    }

    chartData[0].tickValues = tickValues;

    // Sort data chronologically
    chartData[0].data.sort((a, b) => {
      return dayjs(a.x).valueOf() - dayjs(b.x).valueOf();
    });

    return chartData;
  }, [data, dateGrouping, startDate, endDate]);
  return (
    <div
      data-testid={`chart-data-${source[0].id}`}
      className={`flex flex-1 flex-grow flex-col items-center rounded-xl bg-white p-1 ${classes}`}
    >
      {data.length > 0 && (
        <LineChart
          data={source}
          classes="h-full w-full"
          dataPointCount={data.length}
          tooltip={({ point }) => (
            <LineChartTooltipDisplay
              type={type}
              title={title}
              point={point}
              unit={unit}
              format={tooltipFormat}
            />
          )}
          format={format}
          onHover={onHover}
        />
      )}
    </div>
  );
};

export const determineDateGrouping = (
  startDate?: string,
  endDate?: string,
): "day" | "week" | "month" | "quarter" | "year" => {
  // Default to day grouping if dates are invalid
  if (!startDate || !endDate) return "day";

  try {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    // Check if dates are valid
    if (!start.isValid() || !end.isValid()) return "day";

    // Calculate the duration in days
    const durationDays = end.diff(start, "day") + 1; // Include both start and end dates

    // Determine grouping based on duration
    if (durationDays <= 31) {
      return "day";
    } else if (durationDays <= 92) {
      return "week";
    } else if (durationDays <= 365) {
      return "month";
    } else if (durationDays <= 3 * 365) {
      return "quarter";
    } else {
      return "year";
    }
  } catch (error) {
    console.error("Error determining date grouping:", error);
    return "day"; // Default to day grouping if there's an error
  }
};

const formatToDayDate = (
  dateString: string,
  currentFormat: string,
  returnAsStringFormat?: boolean,
  startDate?: string,
  endDate?: string,
): string => {
  // Return as is if already in proper date format or invalid
  if (!dateString) {
    return dateString;
  }

  try {
    // Week format: "YYYY-WW" (e.g., "2025-26")
    if (/^\d{4}-\d{1,2}$/.test(dateString) && currentFormat === "week") {
      const [year, week] = dateString.split("-").map(Number);

      const weekStart = dayjs().year(year).isoWeek(week).startOf("isoWeek");

      const weekEnd = weekStart.add(6, "days");
      // Default to Monday of the week
      let targetDate = weekStart;
      // Determine actual start and end dates for this week
      let actualStart = weekStart;
      let actualEnd = weekEnd;
      // Check if startDate belongs to this week
      if (startDate) {
        const startDateObj = dayjs(startDate);
        if (
          startDateObj.isValid() &&
          startDateObj.isSameOrAfter(weekStart, "day") &&
          startDateObj.isSameOrBefore(weekEnd, "day")
        ) {
          targetDate = startDateObj; // Use the actual startDate
          actualStart = startDateObj;
        }
      }

      // Check if endDate belongs to this week (takes priority)
      if (endDate) {
        const endDateObj = dayjs(endDate);
        if (
          endDateObj.isValid() &&
          endDateObj.isSameOrAfter(weekStart, "day") &&
          endDateObj.isSameOrBefore(weekEnd, "day")
        ) {
          targetDate = endDateObj; // Use the actual endDate
          actualEnd = endDateObj;
        }
      }
      if (returnAsStringFormat === true) {
        // Format the actual start and end dates
        const startFormatted = actualStart.format("DD MMM");
        const endFormatted = actualEnd.format("DD MMM");

        return `Week ${week}, ${year} (${startFormatted}-${endFormatted})`;
      }

      // Return the Monday of that week for chart positioning
      return targetDate.format("DD MMM YYYY");
    }

    // Month format: "YYYY-MM" (e.g., "2025-06")
    if (/^\d{4}-\d{2}$/.test(dateString) && currentFormat === "month") {
      const [year, month] = dateString.split("-").map(Number);
      let dayDisplay = 30;
      if (startDate) {
        const startDateObj = dayjs(startDate);
        if (
          startDateObj.isValid() &&
          startDateObj.year() === year &&
          startDateObj.month() === month - 1
        ) {
          // month() is 0-indexed
          dayDisplay = startDateObj.date(); // Use the actual day from startDate
        }
      }
      if (endDate) {
        const endDateObj = dayjs(endDate);
        if (
          endDateObj.isValid() &&
          endDateObj.year() === year &&
          endDateObj.month() === month - 1
        ) {
          // month() is 0-indexed
          dayDisplay = endDateObj.date(); // Use the actual day from endDate
        }
      }
      if (dayDisplay === 30 && month === 2) {
        dayDisplay = 28; // February
      }
      if (returnAsStringFormat === true) {
        return dayjs(new Date(year, month - 1, dayDisplay)).format("MMM YYYY");
      }
      // Create date for last day of the month (months are 0-indexed in JS)
      return dayjs(new Date(year, month - 1, dayDisplay)).format("DD MMM YYYY");
    }

    // Quarter format: "YYYY-QN" (e.g., "2025-Q2")
    if (/^\d{4}-Q[1-4]$/.test(dateString) && currentFormat === "quarter") {
      const [year, quarterPart] = dateString.split("-Q");
      const quarter = parseInt(quarterPart, 10);
      const firstMonth = (quarter - 1) * 3; // 0-indexed
      const lastMonth = firstMonth + 2;

      // Default to first day of quarter
      let targetDate = dayjs(new Date(parseInt(year, 10), firstMonth, 30));

      // Check if startDate belongs to this quarter
      if (startDate) {
        const startDateObj = dayjs(startDate);
        if (
          startDateObj.isValid() &&
          startDateObj.year() === parseInt(year, 10) &&
          startDateObj.month() >= firstMonth &&
          startDateObj.month() <= lastMonth
        ) {
          targetDate = startDateObj; // Use the actual startDate
        }
      }

      // Check if endDate belongs to this quarter (takes priority)
      if (endDate) {
        const endDateObj = dayjs(endDate);
        if (
          endDateObj.isValid() &&
          endDateObj.year() === parseInt(year, 10) &&
          endDateObj.month() >= firstMonth &&
          endDateObj.month() <= lastMonth
        ) {
          targetDate = endDateObj; // Use the actual endDate
        }
      }
      if (returnAsStringFormat === true) {
        // Calculate the first and last month of the quarter (0-indexed)
        const firstMonth = (quarter - 1) * 3;
        const lastMonth = firstMonth + 2;

        // Get month names
        const startMonth = dayjs(
          new Date(parseInt(year, 10), firstMonth, 1),
        ).format("MMM");
        const endMonth = dayjs(
          new Date(parseInt(year, 10), lastMonth, 1),
        ).format("MMM");

        // Return in format: "Q4 2025 (Oct-Dec)"
        return `Q${quarter} ${year} (${startMonth}-${endMonth})`;
      }

      return targetDate.format("DD MMM YYYY");
    }

    // Year format: "YYYY" (e.g., "2025")
    if (/^\d{4}$/.test(dateString) && currentFormat === "year") {
      const yearNum = parseInt(dateString, 10);

      // Default to first day of year
      let targetDate = dayjs(new Date(yearNum, 0, 30)); // January 30th

      // Check if startDate belongs to this year
      if (startDate) {
        const startDateObj = dayjs(startDate);
        if (startDateObj.isValid() && startDateObj.year() === yearNum) {
          targetDate = startDateObj; // Use the actual startDate
        }
      }

      // Check if endDate belongs to this year (takes priority)
      if (endDate) {
        const endDateObj = dayjs(endDate);
        if (endDateObj.isValid() && endDateObj.year() === yearNum) {
          targetDate = endDateObj; // Use the actual endDate
        }
      }

      if (returnAsStringFormat === true) {
        return dateString; // Return year as is for display format
      }

      // Return the determined target date
      return targetDate.format("DD MMM YYYY");
    }

    // If it's already a valid date, format it
    if (dayjs(dateString).isValid()) {
      return dayjs(dateString).format("DD MMM YYYY");
    }

    // Return original string if no format matched
    return dateString;
  } catch (error) {
    console.error("Error converting date format:", error);
    return dateString; // Return unchanged if conversion fails
  }
};
